import classNames from 'classnames';
import {
  getLiveBettingSportName,
  LIVE_BETTING_SPORT_HEADER_LABELS,
  TOOLTIP_DELAY_SHOW,
} from 'config/constants';
import { sportTranslationKey } from 'helpers/sportI18n';
import { Tooltip } from 'react-tooltip';
import styles from './SportsHeader.module.css';
import { Sport } from 'types/Sport';
import { useTranslation } from 'react-i18next';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import type { ThemeIconKey } from 'lib/themeIcons';
import type { ReactNode } from 'react';

interface Props {
  sport: Sport;
  onExpandClick: () => void;
  titleOverride?: string;
  labelsOverride?: string[];
  hideChevron?: boolean;
  iconOverrideKey?: ThemeIconKey;
  /** Shown over the first odds column instead of its name: the list that
   *  picks which market the column shows. */
  firstColumn?: ReactNode;
}

export const SportsHeader = ({
  sport,
  onExpandClick,
  titleOverride,
  labelsOverride,
  hideChevron = false,
  iconOverrideKey,
  firstColumn,
}: Props) => {
  const { t } = useTranslation();
  const sid = sport.id ?? 1;
  const sportTitle =
    titleOverride ??
    t(sportTranslationKey(sid), {
      defaultValue: sport.name ?? getLiveBettingSportName(sid),
    });

  const sportHeaderLabels = labelsOverride ??
    LIVE_BETTING_SPORT_HEADER_LABELS.get(sport.id) ?? ['2_way', '', '', ''];

  return (
    <div className={styles.header}>
      <div className={styles.sportInfo}>
        {iconOverrideKey ? (
          <ThemeIcon
            icon={iconOverrideKey}
            alt={sportTitle}
            variant="inline"
            tone="chrome"
          />
        ) : (
          <SportIcon
            sportId={sid}
            tone="chrome"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = getDefaultSportIconSrc();
            }}
            alt={sportTitle}
          />
        )}
        <span>{sportTitle}</span>
        {sport.count && <span className={styles.count}>{sport.count}</span>}
      </div>
      {sportHeaderLabels.map((x, labelIndex) => (
        <div className={styles.betTitle} key={`bet-title-${sid}-${labelIndex}`}>
          {labelIndex === 0 && firstColumn ? (
            <div className={styles.firstColumnPick}>{firstColumn}</div>
          ) : (
            !!x && (
              <>
                {t(`${x}_label`)}
                {(() => {
                  const specificKey = `${x}_${sid}_live_tooltip`;
                  const specific = t(specificKey);
                  const generic2WayKey = '2_way_default_live_tooltip';
                  const generic2Way = t(generic2WayKey);
                  const tooltipBody =
                    specific !== specificKey
                      ? specific
                      : x === '2_way' && generic2Way !== generic2WayKey
                        ? generic2Way
                        : null;
                  const tooltipId = `mkt-tt-${sid}-${labelIndex}`;
                  return (
                    tooltipBody && (
                      <>
                        <div
                          data-tooltip-id={tooltipId}
                          data-tooltip-content={tooltipBody}
                          data-tooltip-place="bottom"
                        >
                          <ThemeIcon
                            icon="info-svg"
                            alt="Info"
                            variant="inline"
                            tone="chrome"
                          />
                        </div>
                        <Tooltip
                          id={tooltipId}
                          delayShow={TOOLTIP_DELAY_SHOW}
                          className={classNames('tooltip', styles.greenBorder)}
                          arrowColor="#079c20"
                        />
                      </>
                    )
                  );
                })()}
              </>
            )
          )}
        </div>
      ))}
      {!hideChevron && (
        <div className={styles.chevronDown} onClick={onExpandClick}>
          <div />
        </div>
      )}
    </div>
  );
};
